import { useTranslation } from "react-i18next";
import defaultContent from "../../../content/ssiUplineReport";
import { useAppDispatch } from "../../../store";
import { SearchSelect, SearchSelectItem } from "../../../common/components/SearchSelect/SearchSelect";
import { ExportButton } from "../Export/ExportButton";
import { SsiOverviewResponse } from "../../../services/StrongStart/ssiAPI.types";
import { useCallback, useEffect, useState } from "react";
import { SSIUplineResponse } from "../../../services/SSIUpline/ssiUplineAPI.types";
import { setActiveTabView, ssiUplineReportTabs } from "../../../reducers/ssiUplineReport";
import { ExportListResponse } from "../../../services/ExportV2/exportV2API.types";
import { SSIUplineExportModal } from "../../ServerSideExport/SSIUplineExportModal/SSIUplineExportModal";
import { OpenExportV2ModalEvent } from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { track } from "../../../common/util/tealium";
import TEALIUM from "../../../common/enums/tealium-enums";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";
import { storedLocale } from "../../Authentication/util";

type recordLength = {
  inWindow: number;
  preWindow: number;
  outOfProgram: number;
  earnedPayment: number;
};

type Props = {
  pageData: SSIUplineResponse;
  searchPool: SsiOverviewResponse[] | undefined;
  windowWidth: number;
  isLoading: boolean;
  showExportButton: boolean;
  activeView: ssiUplineReportTabs;
  ssiExportData: ExportListResponse | undefined;
  recordLength: recordLength;
  setSelectedAboData: (num: string, name: string) => void;
  selectedAboName: string;
};

export default function MobileSearch({
  pageData,
  searchPool,
  windowWidth,
  isLoading,
  showExportButton,
  activeView,
  ssiExportData,
  recordLength,
  setSelectedAboData,
  selectedAboName,
}: Props) {
  const { t } = useTranslation(["ssiUplineReport"]);
  const dispatch = useAppDispatch();
  const mobileSearchBreakpoint = 1300;
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [modalOpenedByElement, setModalOpenedByElement] = useState<HTMLElement>();
  const searchClass = windowWidth <= mobileSearchBreakpoint ? "mobileSearch" : "searchContainer";

  const checkInTabs = (key: string) => {
    const arr = Object.values(pageData.InWindow);
    for (const inWindowTree of arr) {
      if (inWindowTree.length > 0) {
        // search for the abo using the key
        const foundIndex = inWindowTree.findIndex((abo: SsiOverviewResponse) => abo.Abo === key);

        if (foundIndex > -1) {
          // thing is in InWindow
          dispatch(setActiveTabView(ssiUplineReportTabs.IN_SSI_PROGRAM_TAB));
          return;
        }
      }
    }

    const arrOop = Object.values(pageData.OutOfProgram);
    for (const outOfProgramTree of arrOop) {
      if (outOfProgramTree.length > 0) {
        // search for the abo using the key
        const foundIndex = outOfProgramTree.findIndex((abo: SsiOverviewResponse) => abo.Abo === key);

        if (foundIndex > -1) {
          // thing is in OutOfProgram
          dispatch(setActiveTabView(ssiUplineReportTabs.OUT_SSI_PROGRAM_TAB));
          return;
        }
      }
    }
    // Check EarnedPayment groups
    if (pageData.EarnedPayment?.Groups) {
      for (const group of pageData.EarnedPayment.Groups) {
        const foundIndex = (group.ABOs ?? []).findIndex((abo) => abo.Abo === key);
        if (foundIndex > -1) {
          dispatch(setActiveTabView(ssiUplineReportTabs.EARNED_PAYMENT_TAB));
          return;
        }
      }
    }

    // if not found in InWindow, must be in preWindow
    dispatch(setActiveTabView(ssiUplineReportTabs.GRACE_PERIOD_TAB));
  };

  const showExportModal = useCallback((event: CustomEventInit) => {
    setIsExportModalOpen(event.detail.open);
    setModalOpenedByElement(event.detail.target);
    track(TEALIUM.U_TAG_METHOD.LINK, "", "export_ssi_report", "ssi_upline_report", "", "export_ssi_upline_report");
  }, []);

  useEffect(() => {
    window.addEventListener(OpenExportV2ModalEvent, showExportModal);
    return () => {
      window.removeEventListener(OpenExportV2ModalEvent, showExportModal);
    };
  }, [showExportModal]);

  const handleSearchCallback = (searchText: string): SearchSelectItem[] => {
    if (searchText === "") {
      setSelectedAboData("", "");
    }

    return (
      searchPool
        ?.filter((item: SsiOverviewResponse) => {
          return (
            item.AboName?.toLowerCase().includes(searchText.toLowerCase()) || item.Abo?.toString().includes(searchText)
          );
        })
        .map((r) => {
          const sublabel = `${r.Abo} | ${getCountryNameFromCode(r.IsoCountry, storedLocale())}`;
          return { key: r.Abo, label: r.AboName, sublabel };
        }) ?? []
    );
  };

  const handleSelectionCallback = (key: string): { displayName: string } => {
    checkInTabs(key);

    const foundData = searchPool?.find((item: SsiOverviewResponse) => item.Abo === key);

    if (foundData) {
      setSelectedAboData(foundData.Abo, foundData.AboName);
    }

    return { displayName: foundData?.AboName ?? "" };
  };

  const onCloseCallbackFunction = () => {
    setIsExportModalOpen(false);
    setTimeout(() => {
      if (modalOpenedByElement) {
        modalOpenedByElement.focus();
      }
    }, 100);
  };

  const clearSearchState = () => {
    setSelectedAboData("", "");
  };

  const handleExportButtonClick = () => {
    setIsExportModalOpen(true);
    exportTealiumTrack();
  };

  const exportTealiumTrack = () => {
    track(TEALIUM.U_TAG_METHOD.LINK, "", "export_ssi_upline_report", "ssi_upline_report", "", "export_ssi_report");
  };

  const searchTealiumTrack = () => {
    track(TEALIUM.U_TAG_METHOD.LINK, "", "abo_search", "ssi_upline_report", "", "search_ssi_report");
  };

  const renderSearchSelect = () => {
    return (
      <div onFocusCapture={searchTealiumTrack}>
        <SearchSelect
          placeholderText={t("searchPlaceholder", defaultContent["searchPlaceholder"]).toString()}
          clearSearchAriaLabel={t("clearSearch", defaultContent["clearSearch"]).toString()}
          searchCallback={handleSearchCallback}
          selectionCallback={handleSelectionCallback}
          disabled={isLoading}
          searchText={selectedAboName}
          clear={clearSearchState}
        />
      </div>
    );
  };

  const checkTabRecordLength = () => {
    if (activeView === ssiUplineReportTabs.GRACE_PERIOD_TAB) {
      return !(recordLength.preWindow > 0);
    } else if (activeView === ssiUplineReportTabs.IN_SSI_PROGRAM_TAB) {
      return !(recordLength.inWindow > 0);
    } else if (activeView === ssiUplineReportTabs.OUT_SSI_PROGRAM_TAB) {
      return !(recordLength.outOfProgram > 0);
    } else if (activeView === ssiUplineReportTabs.EARNED_PAYMENT_TAB) {
      return !(recordLength.earnedPayment > 0);
    }
    return false;
  };

  const renderSSIUPlineExportModal = () => {
    return (
      <SSIUplineExportModal
        isFetching={isLoading}
        open={isExportModalOpen}
        onCloseCallbackFnInParent={onCloseCallbackFunction}
        activeView={activeView}
      />
    );
  };

  return (
    <div className={searchClass}>
      {renderSearchSelect()}
      <ExportButton
        visible={showExportButton}
        disabled={isLoading || checkTabRecordLength()}
        exportList={ssiExportData?.Exports || []}
        onClickFunction={handleExportButtonClick}
      />
      {renderSSIUPlineExportModal()}
    </div>
  );
}
